import { AlertCircle, CheckCircle2 } from "lucide-react";

export function Field({ label, htmlFor, required, error, hint, children, className = "" }) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="mb-2 block text-sm font-bold text-bka-navy">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-bka-ink/60">{hint}</p>}
      {error && (
        <p id={`${htmlFor}-error`} className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-red-600">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </p>
      )}
    </div>
  );
}

const baseInput =
  "w-full rounded-xl border bg-white px-4 py-3 text-sm text-bka-ink transition-colors placeholder:text-bka-ink/40 focus:border-bka-emerald focus:outline-none focus:ring-2 focus:ring-bka-emerald/20";

export function TextInput({ error, ...props }) {
  return (
    <input
      {...props}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${props.id}-error` : undefined}
      className={`${baseInput} ${error ? "border-red-400" : "border-slate-200"}`}
    />
  );
}

export function TextArea({ error, rows = 5, ...props }) {
  return (
    <textarea
      {...props}
      rows={rows}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${props.id}-error` : undefined}
      className={`${baseInput} resize-y ${error ? "border-red-400" : "border-slate-200"}`}
    />
  );
}

export function SelectInput({ error, children, ...props }) {
  return (
    <select
      {...props}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${props.id}-error` : undefined}
      className={`${baseInput} ${error ? "border-red-400" : "border-slate-200"}`}
    >
      {children}
    </select>
  );
}

export function Honeypot({ value, onChange }) {
  return (
    <div className="absolute left-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
      <label htmlFor="website-url-hp">Leave this field empty</label>
      <input id="website-url-hp" name="website-url-hp" tabIndex={-1} value={value} onChange={onChange} />
    </div>
  );
}

export function ConsentCheck({ id, checked, onChange, children, error, testId }) {
  return (
    <div>
      <label htmlFor={id} className="flex cursor-pointer items-start gap-3 text-sm text-bka-ink/85">
        <input
          id={id}
          data-testid={testId}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[color:var(--bka-emerald)]"
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <span>{children}</span>
      </label>
      {error && (
        <p id={`${id}-error`} className="mt-1.5 flex items-center gap-1.5 text-xs font-semibold text-red-600">
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </p>
      )}
    </div>
  );
}

export function FormBanner({ status, message, testId = "form-banner" }) {
  if (!message) return null;
  const ok = status === "ok";
  return (
    <div
      data-testid={testId}
      role={ok ? "status" : "alert"}
      className={`flex items-start gap-3 rounded-xl border p-4 text-sm font-semibold ${
        ok ? "border-bka-emerald/30 bg-bka-aqua text-bka-teal" : "border-red-200 bg-red-50 text-red-700"
      }`}
    >
      {ok ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
      ) : (
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      )}
      <span>{message}</span>
    </div>
  );
}
